import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { toast } from 'sonner';
import {
  ZoomIn, ZoomOut, Upload, Type, RotateCcw, Grid3X3, LayoutGrid,
  ImageIcon, ArrowRight, Download, Move, Sparkles, Loader2,
  Printer, FileText, Trash2, Undo2, Redo2, Eye, Square, Circle, RectangleHorizontal
} from 'lucide-react';
import { FONTS, PHOTO_GRIDS } from './constants';

export const AssetSidebar = ({ engine, template, availablePhotos, onNextStep, onPreview }) => {
  const {
    selectedElement, selectedGridSlot, selectedPhotoPlaceholder,
    isMultiSided, currentSide, showGridPicker, autoMarketingLoading,
    editingText, selectedFont, showPhotoPicker, photoPickerTarget,
    scale, brandKit, stageRef,
    setScale, setShowGridPicker, setShowPhotoPicker, setPhotoPickerTarget,
    handleSideSwitch, addUserText, addPhotoGrid, addRect, addCircle,
    handleTextChange, handleFontChange, updateElement,
    handleAutoMarketing, resetToOriginal, downloadDesign,
    handleImageUpload, handleSelectFromAvailablePhotos,
    handleGridSlotSelect, handleGridZoomChange,
    fileInputRef, setSelectedId, elements, undo, redo, historyRef, redoStackRef
  } = engine;

  return (
    <div className="lg:w-72 space-y-4 p-4 border border-border/50 rounded-lg bg-card overflow-y-auto max-h-[700px]"
      data-testid="asset-sidebar">
      <h3 className="font-semibold text-lg">Customize Template</h3>

      {/* Multi-sided selector */}
      {isMultiSided && (
        <div className="space-y-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
          <Label className="text-sm font-medium flex items-center gap-2">
            <RotateCcw className="h-4 w-4" /> Template Side
          </Label>
          <div className="flex gap-2">
            <Button variant={currentSide === 'front' ? 'default' : 'outline'} size="sm"
              onClick={() => handleSideSwitch('front')} className="flex-1" data-testid="side-front-btn">Front</Button>
            <Button variant={currentSide === 'back' ? 'default' : 'outline'} size="sm"
              onClick={() => handleSideSwitch('back')} className="flex-1" data-testid="side-back-btn">Back</Button>
          </div>
          <p className="text-xs text-muted-foreground">Editing: {currentSide === 'front' ? 'Front' : 'Back'} side</p>
        </div>
      )}

      {/* Add Elements */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Add Elements</Label>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={addUserText} data-testid="add-text-button">
            <Type className="h-4 w-4 mr-1" /> Text
          </Button>
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="add-image-button">
            <Upload className="h-4 w-4 mr-1" /> Image
          </Button>
          <Button variant="outline" size="sm" onClick={() => addRect(false)} data-testid="add-rect-button">
            <Square className="h-4 w-4 mr-1" /> Rectangle
          </Button>
          <Button variant="outline" size="sm" onClick={() => addCircle()} data-testid="add-circle-button">
            <Circle className="h-4 w-4 mr-1" /> Circle
          </Button>
          <Button variant="outline" size="sm" onClick={() => addRect(true)} data-testid="add-rounded-rect-button">
            <RectangleHorizontal className="h-4 w-4 mr-1" /> Rounded
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowGridPicker(!showGridPicker)}
          className="w-full" data-testid="add-grid-button">
          <LayoutGrid className="h-4 w-4 mr-2" /> Photo Grid
        </Button>
        <Button variant="default" size="sm" onClick={handleAutoMarketing} disabled={autoMarketingLoading}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
          data-testid="auto-marketing-button">
          {autoMarketingLoading
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing...</>
            : <><Sparkles className="h-4 w-4 mr-2" /> Auto-Marketing</>}
        </Button>
        <p className="text-xs text-muted-foreground">AI analyzes your photos and creates a catchy headline</p>
      </div>

      {/* Grid Picker */}
      {showGridPicker && (
        <div className="space-y-2 p-3 border border-border/50 rounded-lg bg-muted/20">
          <Label className="text-sm font-medium">Select Grid Layout</Label>
          <div className="grid grid-cols-2 gap-2">
            {PHOTO_GRIDS.map(grid => (
              <Button key={grid.id} variant="outline" size="sm"
                className="h-auto py-2 px-2 flex flex-col items-center"
                onClick={() => addPhotoGrid(grid)} data-testid={`grid-${grid.id}`}>
                <Grid3X3 className="h-4 w-4 mb-1" />
                <span className="text-xs text-center">{grid.name}</span>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Property Photos */}
      {availablePhotos.length > 0 && (
        <div className="space-y-2 p-3 border border-primary/30 rounded-lg bg-primary/5">
          <Label className="text-sm font-medium flex items-center gap-2">
            <ImageIcon className="h-4 w-4" /> Property Photos
            <span className="text-xs text-muted-foreground font-normal">({availablePhotos.length})</span>
          </Label>
          <p className="text-xs text-muted-foreground">Drag photos to canvas or click a slot then select</p>
          <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
            {availablePhotos.slice(0, 9).map((photo, idx) => (
              <div key={idx} draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/x-photo-url', photo);
                  e.dataTransfer.setData('text/plain', photo);
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                className={`relative aspect-square rounded-md overflow-hidden cursor-grab border-2 transition-all hover:border-primary active:cursor-grabbing ${
                  idx === 0 ? 'border-primary' : 'border-transparent'
                }`}
                onClick={() => {
                  if (photoPickerTarget) handleSelectFromAvailablePhotos(photo);
                  else toast.info('Click a photo slot on the canvas first, or drag the photo directly');
                }}
                data-testid={`property-photo-${idx}`}>
                <img src={photo} alt={`Property ${idx + 1}`}
                  className="w-full h-full object-cover pointer-events-none"
                  onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=200&q=60'; }} />
                {idx === 0 && (
                  <div className="absolute bottom-0 left-0 right-0 bg-primary/80 text-primary-foreground text-[10px] text-center py-0.5">Main</div>
                )}
              </div>
            ))}
          </div>
          {availablePhotos.length > 9 && (
            <p className="text-xs text-center text-muted-foreground">+{availablePhotos.length - 9} more photos</p>
          )}
        </div>
      )}

      {/* Photo Picker Modal */}
      {showPhotoPicker && availablePhotos.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowPhotoPicker(false)}>
          <div className="bg-card rounded-lg p-4 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()} data-testid="photo-picker-modal">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-medium">Select Photo</Label>
              <Button variant="ghost" size="sm" onClick={() => setShowPhotoPicker(false)}>&#x2715;</Button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Choose from your listing or upload a new one</p>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {availablePhotos.map((photo, idx) => (
                <div key={idx}
                  className="relative aspect-square rounded-md overflow-hidden cursor-pointer border-2 border-transparent hover:border-primary transition-all"
                  onClick={() => handleSelectFromAvailablePhotos(photo)}
                  data-testid={`picker-photo-${idx}`}>
                  <img src={photo} alt={`Property ${idx + 1}`} className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=200&q=60'; }} />
                  {idx === 0 && (
                    <div className="absolute bottom-0 left-0 right-0 bg-primary/80 text-primary-foreground text-[10px] text-center py-0.5">Main Photo</div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1"
                onClick={() => { setShowPhotoPicker(false); fileInputRef.current?.click(); }}>
                <Upload className="h-4 w-4 mr-1" /> Upload Custom
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowPhotoPicker(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Selected Element Properties */}
      {selectedElement && (
        <div className="space-y-3 pt-4 border-t border-border/50">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">
              Edit {selectedElement.type === 'text' ? 'Text' : selectedElement.type === 'photoGrid' ? 'Photo Grid' : selectedElement.type === 'rect' ? 'Rectangle' : selectedElement.type === 'circle' ? 'Circle' : 'Image'}
            </Label>
            <Button variant="destructive" size="sm" data-testid="delete-element-button"
              onClick={() => {
                engine.deleteElement(selectedElement.id);
                toast.success('Element deleted');
              }}>
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          </div>

          {/* Text properties */}
          {selectedElement.type === 'text' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Text Content</Label>
                <Textarea value={editingText} onChange={(e) => handleTextChange(e.target.value)}
                  placeholder="Enter your text..." rows={3} className="text-sm mt-1" data-testid="edit-text-input" />
              </div>
              <div>
                <Label className="text-xs">Font Family</Label>
                <Select value={selectedFont} onValueChange={handleFontChange}>
                  <SelectTrigger className="h-9 mt-1" data-testid="font-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {FONTS.map(f => (
                      <SelectItem key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Color</Label>
                  <Input type="color" value={selectedElement.fill || '#000000'}
                    onChange={(e) => updateElement({ ...selectedElement, fill: e.target.value })} className="h-9 w-full mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Size</Label>
                  <Input type="number" value={selectedElement.fontSizeBase || selectedElement.fontSize || 32}
                    onChange={(e) => {
                      const v = parseInt(e.target.value) || 32;
                      updateElement({ ...selectedElement, fontSize: v, fontSizeBase: v });
                    }}
                    className="h-9 mt-1" min={12} max={200} />
                </div>
              </div>
              <label className="flex items-start gap-2 text-xs cursor-pointer pt-1" data-testid="autofit-toggle-row">
                <input
                  type="checkbox"
                  checked={selectedElement.autoFit !== false && selectedElement.autoFit === true}
                  onChange={(e) => {
                    const next = { ...selectedElement, autoFit: e.target.checked };
                    if (e.target.checked) next.fontSizeBase = selectedElement.fontSizeBase || selectedElement.fontSize;
                    updateElement(next);
                  }}
                  className="mt-0.5"
                  data-testid="autofit-toggle"
                />
                <span className="text-muted-foreground">
                  <span className="font-medium text-foreground">Keep on one line</span>
                  <span className="block text-[11px]">Shrinks font automatically if the text gets too long (great for addresses).</span>
                </span>
              </label>
            </div>
          )}

          {/* Image properties */}
          {selectedElement.type === 'image' && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
                  className="flex-1" data-testid="replace-image-button">
                  <Upload className="h-4 w-4 mr-1" /> Replace
                </Button>
                <Button variant="destructive" size="sm" data-testid="delete-image-button"
                  onClick={() => {
                    if (selectedElement.wasPhotoPlaceholder) {
                      updateElement({ ...selectedElement, type: 'photoPlaceholder', src: null });
                      setSelectedId(null);
                      toast.success('Photo removed');
                    } else {
                      engine.deleteElement(selectedElement.id);
                      toast.success('Image deleted');
                    }
                  }}>
                  <Trash2 className="h-4 w-4 mr-1" /> {selectedElement.wasPhotoPlaceholder ? 'Remove' : 'Delete'}
                </Button>
              </div>
              {selectedElement.src && selectedElement.wasPhotoPlaceholder && (
                <>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs flex items-center gap-1"><ZoomIn className="h-3 w-3" /> Zoom</Label>
                      <span className="text-xs text-muted-foreground">{Math.round((selectedElement.imageZoom || 1) * 100)}%</span>
                    </div>
                    <Slider min={50} max={200} step={5}
                      value={[Math.round((selectedElement.imageZoom || 1) * 100)]}
                      onValueChange={(v) => updateElement({ ...selectedElement, imageZoom: v[0] / 100 })} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs flex items-center gap-1"><Move className="h-3 w-3" /> Position</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Horizontal</Label>
                        <Slider min={0} max={100} step={2}
                          value={[Math.round((selectedElement.imageOffsetX ?? 0.5) * 100)]}
                          onValueChange={(v) => updateElement({ ...selectedElement, imageOffsetX: v[0] / 100 })} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Vertical</Label>
                        <Slider min={0} max={100} step={2}
                          value={[Math.round((selectedElement.imageOffsetY ?? 0.5) * 100)]}
                          onValueChange={(v) => updateElement({ ...selectedElement, imageOffsetY: v[0] / 100 })} />
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="w-full text-xs"
                    onClick={() => {
                      updateElement({ ...selectedElement, imageOffsetX: 0.5, imageOffsetY: 0.5, imageZoom: 1 });
                      toast.success('Image position reset');
                    }}>
                    <RotateCcw className="h-3 w-3 mr-1" /> Reset Position
                  </Button>
                </>
              )}
              {selectedElement.isPlaceholder && (
                <p className="text-xs text-amber-600">This is a placeholder. Click to upload your image.</p>
              )}
            </div>
          )}

          {/* Shape properties (rect/circle) */}
          {(selectedElement.type === 'rect' || selectedElement.type === 'circle') && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Fill Color</Label>
                  <Input type="color" value={selectedElement.fill || '#8B5CF6'}
                    onChange={(e) => updateElement({ ...selectedElement, fill: e.target.value })}
                    className="h-9 w-full mt-1" data-testid="shape-fill-input" />
                </div>
                <div>
                  <Label className="text-xs">Opacity</Label>
                  <Input type="number" value={Math.round((selectedElement.opacity ?? 1) * 100)}
                    onChange={(e) => updateElement({ ...selectedElement, opacity: Math.max(0, Math.min(100, parseInt(e.target.value) || 100)) / 100 })}
                    className="h-9 mt-1" min={0} max={100} data-testid="shape-opacity-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Stroke Color</Label>
                  <Input type="color" value={selectedElement.stroke || '#000000'}
                    onChange={(e) => updateElement({ ...selectedElement, stroke: e.target.value })}
                    className="h-9 w-full mt-1" data-testid="shape-stroke-input" />
                </div>
                <div>
                  <Label className="text-xs">Stroke Width</Label>
                  <Input type="number" value={selectedElement.strokeWidth || 0}
                    onChange={(e) => updateElement({ ...selectedElement, strokeWidth: Math.max(0, parseInt(e.target.value) || 0) })}
                    className="h-9 mt-1" min={0} max={20} data-testid="shape-stroke-width-input" />
                </div>
              </div>
              {selectedElement.type === 'rect' && (
                <div>
                  <Label className="text-xs">Corner Radius</Label>
                  <Slider min={0} max={60} step={1}
                    value={[selectedElement.cornerRadius || 0]}
                    onValueChange={([v]) => updateElement({ ...selectedElement, cornerRadius: v })}
                    data-testid="shape-corner-radius" />
                  <span className="text-xs text-muted-foreground">{selectedElement.cornerRadius || 0}px</span>
                </div>
              )}
              {selectedElement.type === 'circle' && (
                <div>
                  <Label className="text-xs">Radius</Label>
                  <Input type="number" value={selectedElement.radius || 60}
                    onChange={(e) => updateElement({ ...selectedElement, radius: Math.max(5, parseInt(e.target.value) || 60) })}
                    className="h-9 mt-1" min={5} max={500} data-testid="shape-radius-input" />
                </div>
              )}
            </div>
          )}

          {/* Photo Grid properties */}
          {selectedElement.type === 'photoGrid' && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Click on a slot in the grid to select it, then choose a photo.</p>
              {selectedGridSlot && (
                <div className="space-y-3">
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-purple-800 font-medium">Slot {selectedGridSlot.slotIndex + 1}</p>
                    {selectedElement.slots[selectedGridSlot.slotIndex]?.src ? (
                      <p className="text-xs text-purple-600 mt-1">Drag to pan. Use the floating zoom slider on the canvas.</p>
                    ) : (
                      <p className="text-xs text-purple-600 mt-1">Select a property photo below or upload a custom image.</p>
                    )}
                  </div>
                  {selectedElement.slots[selectedGridSlot.slotIndex]?.src && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs flex items-center gap-1"><ZoomIn className="h-3 w-3" /> Zoom</Label>
                        <span className="text-xs text-muted-foreground">
                          {Math.round((selectedElement.slots[selectedGridSlot.slotIndex]?.imageZoom || 1) * 100)}%
                        </span>
                      </div>
                      <Slider min={100} max={300} step={5}
                        value={[Math.round((selectedElement.slots[selectedGridSlot.slotIndex]?.imageZoom || 1) * 100)]}
                        onValueChange={(v) => handleGridZoomChange(
                          selectedGridSlot.gridId, selectedGridSlot.slotIndex, v[0] / 100
                        )}
                        data-testid="sidebar-zoom-slider" />
                      <Button variant="ghost" size="sm" className="w-full text-xs"
                        onClick={() => {
                          engine.handleGridPanChange(selectedGridSlot.gridId, selectedGridSlot.slotIndex, 0.5, 0.5);
                          handleGridZoomChange(selectedGridSlot.gridId, selectedGridSlot.slotIndex, 1);
                          toast.success('Reset to center');
                        }}>
                        <RotateCcw className="h-3 w-3 mr-1" /> Reset Position & Zoom
                      </Button>
                    </div>
                  )}
                  {!selectedElement.slots[selectedGridSlot.slotIndex]?.src && (
                    <Button variant="default" size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full bg-purple-600 hover:bg-purple-700" data-testid="upload-grid-photo-button">
                      <Upload className="h-4 w-4 mr-2" /> Upload Photo to Slot {selectedGridSlot.slotIndex + 1}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Photo Placeholder properties */}
          {selectedElement.type === 'photoPlaceholder' && (
            <div className="space-y-2">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-medium">Property Photo Placeholder</p>
                <p className="text-xs text-amber-600 mt-1">Upload your listing photo below.</p>
              </div>
              <Button variant="default" size="sm" onClick={() => fileInputRef.current?.click()}
                className="w-full" data-testid="upload-property-photo-button">
                <Upload className="h-4 w-4 mr-2" /> Upload Property Photo
              </Button>
            </div>
          )}

          {/* Universal Position & Size Controls */}
          {selectedElement && selectedElement.editable && selectedElement.type !== 'photoGrid' && (
            <div className="space-y-2 pt-3 border-t border-border/30">
              <Label className="text-xs font-medium flex items-center gap-1">
                <Move className="h-3 w-3" /> Position & Size
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-muted-foreground">X</Label>
                  <Input type="number" value={Math.round(selectedElement.x || 0)}
                    onChange={(e) => updateElement({ ...selectedElement, x: parseInt(e.target.value) || 0 })}
                    className="h-7 text-xs" data-testid="pos-x-input" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Y</Label>
                  <Input type="number" value={Math.round(selectedElement.y || 0)}
                    onChange={(e) => updateElement({ ...selectedElement, y: parseInt(e.target.value) || 0 })}
                    className="h-7 text-xs" data-testid="pos-y-input" />
                </div>
              </div>
              {selectedElement.width !== undefined && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Width</Label>
                    <Input type="number" value={Math.round(selectedElement.width || 100)}
                      onChange={(e) => updateElement({ ...selectedElement, width: Math.max(10, parseInt(e.target.value) || 100) })}
                      className="h-7 text-xs" min={10} data-testid="size-w-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Height</Label>
                    <Input type="number" value={Math.round(selectedElement.height || 100)}
                      onChange={(e) => updateElement({ ...selectedElement, height: Math.max(10, parseInt(e.target.value) || 100) })}
                      className="h-7 text-xs" min={10} data-testid="size-h-input" />
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" className="flex-1 text-xs h-7"
                  onClick={() => updateElement({ ...selectedElement, x: 0, y: engine.canvasHeight - (selectedElement.height || 50) })}
                  data-testid="pos-bottom-left">
                  Bottom-Left
                </Button>
                <Button variant="ghost" size="sm" className="flex-1 text-xs h-7"
                  onClick={() => updateElement({
                    ...selectedElement,
                    x: Math.round((engine.canvasWidth - (selectedElement.width || 100)) / 2),
                    y: Math.round((engine.canvasHeight - (selectedElement.height || 50)) / 2)
                  })}
                  data-testid="pos-center">
                  Center
                </Button>
                <Button variant="ghost" size="sm" className="flex-1 text-xs h-7"
                  onClick={() => updateElement({
                    ...selectedElement,
                    x: engine.canvasWidth - (selectedElement.width || 100),
                    y: engine.canvasHeight - (selectedElement.height || 50)
                  })}
                  data-testid="pos-bottom-right">
                  Bottom-Right
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Brand Kit */}
      {brandKit?.logo_url && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-xs text-green-700">Your logo from Brand Kit is being used</p>
        </div>
      )}

      {/* Canvas Zoom */}
      <div className="space-y-2 pt-4 border-t border-border/50">
        <Label className="text-sm font-medium">Zoom: {Math.round(scale * 100)}%</Label>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setScale(Math.max(0.2, scale - 0.1))} data-testid="zoom-out-button">
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setScale(Math.min(1, scale + 0.1))} data-testid="zoom-in-button">
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Undo & Redo & Reset */}
      <div className="pt-4 border-t border-border/50 space-y-2">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={undo}
            className="flex-1" data-testid="undo-button"
            disabled={historyRef.current.length === 0}>
            <Undo2 className="h-4 w-4 mr-1" /> Undo
          </Button>
          <Button variant="outline" size="sm" onClick={redo}
            className="flex-1" data-testid="redo-button"
            disabled={redoStackRef.current.length === 0}>
            <Redo2 className="h-4 w-4 mr-1" /> Redo
          </Button>
        </div>
        <Button variant="ghost" size="sm" onClick={resetToOriginal}
          className="w-full text-muted-foreground" data-testid="reset-template-button">
          <RotateCcw className="h-4 w-4 mr-2" /> Reset to Original
        </Button>
      </div>

      {/* Preview */}
      <div className="pt-4 border-t border-border/50">
        <Button variant="outline" size="sm" onClick={onPreview}
          className="w-full" data-testid="preview-button">
          <Eye className="h-4 w-4 mr-2" /> Preview Design
        </Button>
      </div>

      {/* Action Buttons */}
      <div className="pt-4 border-t border-border/50 space-y-2">
        {['flyer', 'postcard', 'brochure'].includes(template?.type) ? (
          <>
            <Button onClick={downloadDesign} className="w-full" data-testid="download-png-button">
              <Download className="h-4 w-4 mr-2" /> Download PNG
            </Button>
            <Button variant="outline" size="sm" className="w-full" data-testid="export-pdf-button"
              onClick={async () => {
                if (!stageRef.current) return;
                toast.info('Generating PDF at 300 DPI...');
                const prevSel = engine.selectedId;
                engine.setSelectedId(null);
                await new Promise(r => setTimeout(r, 100));

                try {
                  const { jsPDF } = await import('jspdf');
                  // 300 DPI: pixelRatio = 300 / 96 ≈ 3.125
                  const dataUrl = stageRef.current.toDataURL({ pixelRatio: 300 / 96 });

                  // Page dimensions in inches
                  const dims = template?.type === 'flyer' ? { w: 8.5, h: 11, o: 'portrait' }
                    : template?.type === 'postcard' ? { w: 6, h: 4, o: 'landscape' }
                    : { w: 11, h: 8.5, o: 'landscape' }; // brochure

                  const pdf = new jsPDF({
                    orientation: dims.o,
                    unit: 'in',
                    format: [dims.w, dims.h]
                  });

                  pdf.addImage(dataUrl, 'PNG', 0, 0, dims.w, dims.h, undefined, 'FAST');
                  pdf.save(`realprop-${template?.type || 'design'}-${Date.now()}.pdf`);
                  toast.success('PDF saved!');
                } catch (e) {
                  console.error('PDF generation error:', e);
                  toast.error('PDF generation failed. Try downloading as PNG instead.');
                }
                engine.setSelectedId(prevSel);
              }}>
              <FileText className="h-4 w-4 mr-2" /> Export PDF
            </Button>
            <Button variant="outline" size="sm" className="w-full" data-testid="print-design-button"
              onClick={() => {
                if (stageRef.current) {
                  const dataUrl = stageRef.current.toDataURL({ pixelRatio: 3 });
                  const pw = window.open('', '_blank', 'width=800,height=600');
                  if (pw) {
                    const ps = template?.type === 'flyer' ? '8.5in 11in' : template?.type === 'postcard' ? '6in 4in' : '11in 8.5in';
                    pw.document.write(`<!DOCTYPE html><html><head><title>Print</title><style>@page{size:${ps};margin:0}*{margin:0;padding:0}body{display:flex;justify-content:center;align-items:center;background:white}img{max-width:100%;height:auto}@media print{img{width:100%}}</style></head><body><img src="${dataUrl}" onload="setTimeout(function(){window.print()},300)"/></body></html>`);
                    pw.document.close();
                  }
                }
                toast.info('Select your printer. Disable headers/footers for clean output.');
              }}>
              <Printer className="h-4 w-4 mr-2" /> Print Design
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" size="sm" onClick={downloadDesign} className="w-full" data-testid="download-design-button">
              <Download className="h-4 w-4 mr-2" /> Save to Device
            </Button>
            {onNextStep && (
              <Button onClick={onNextStep} className="w-full" data-testid="next-step-button">
                Next: Caption & Schedule <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </>
        )}
      </div>

      {/* Tips */}
      <div className="pt-4 border-t border-border/50 text-xs text-muted-foreground space-y-1">
        <p><Move className="h-3 w-3 inline mr-1" /> Drag elements to reposition</p>
        <p>Click on text or images to edit</p>
        <p><Undo2 className="h-3 w-3 inline mr-1" /> Ctrl+Z undo, Ctrl+Shift+Z redo</p>
        <p>Delete / Backspace to remove selected</p>
      </div>
    </div>
  );
};
